import React, { useState, useEffect, useCallback } from "react";
import { useFindMany, useAction, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  VStack,
  HStack,
  Flex,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Tag,
  TagLabel,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  SimpleGrid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
  useToast,
} from "@chakra-ui/react";
import { EditIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { debounce, isSystemAdmin, safeArrayMap } from "./configHelpers";
import { Link } from "react-router-dom";
import CohortEditModal from "./CohortEditModal";
import MemberManagementModal from "./MemberManagementModal";

// Cohort List Component
function CohortList({ institution, onRefresh, enabled = true, session, institutionConfig }) {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY
  const [{ data: cohorts, fetching: loadingCohorts, error: cohortsError }, refetchCohorts] = useFindMany(api.cohort, {
    filter: { institution: { equals: decodeURIComponent(institution) } },
    select: {
      id: true,
      name: true,
      displayName: true,
      cohortType: true,
      academicYear: true,
      startDate: true,
      endDate: true,
      isActive: true,
      maxStudents: true,
      shiftEligibility: true,
      enableClinicalDocNotifications: true,
      cohortAgentAccessEnabled: true,
      cohortAccessibleAgents: true,
      defaultAgentAccessMode: true,
      autoGrantAgentAccess: true,
      escalationMembers: true,
      fisdapCalendarUrl: true,
      calendarSyncEnabled: true,
      lastCalendarSyncAt: true,
      calendarSyncStatus: true,
      createdAt: true,
      updatedAt: true,
    },
    pause: !enabled
  });

  // Fetch cohort memberships for this institution to get agent access information
  const [{ data: cohortMemberships, fetching: loadingMemberships, error: membershipsError }] = useFindMany(api.cohortMembership, {
    filter: {
      active: { equals: true }
      // Note: We can't filter by cohort.institution in the GraphQL query directly
      // The cohorts query already filters by institution, so memberships will be institution-specific
    },
    select: {
      id: true,
      cohort: {
        id: true
      },
      user: {
        id: true,
        firstName: true,
        lastName: true,
        agentAccessMode: true,
        accessibleAgentNames: true,
        assistantAccessMode: true,
        accessibleAssistantIds: true,
      }
    },
    pause: !enabled
  });

  const [{ data: deleteResult, fetching: deleting, error: deleteError }, deleteCohort] = useAction(api.cohort.delete);
  const [{ fetching: repairingDebriefFlags }, runDebriefBackfill] = useGlobalAction(api.backfillDebriefAudioFlags);
  const [expandedCohorts, setExpandedCohorts] = useState(new Set());
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [editCohortModal, setEditCohortModal] = useState({ isOpen: false, cohort: null });
  const [memberModal, setMemberModal] = useState({ isOpen: false, cohort: null });
  const [attemptedDebriefRepair, setAttemptedDebriefRepair] = useState(false);
  const toast = useToast();

  // Debounce refetch to prevent multiple rapid refreshes
  const debouncedRefetch = useCallback(
    debounce(() => {
      refetchCohorts();
    }, 1000),
    [refetchCohorts]
  );

  // Store refresh function for parent component
  useEffect(() => {
    if (onRefresh) {
      onRefresh(refetchCohorts);
    }
  }, [onRefresh, refetchCohorts]);

  // Self-heal legacy NULL required flag corruption in production.
  useEffect(() => {
    const message = cohortsError?.message || "";
    const isDebriefFlagCorruption =
      message.includes("GGT_INVALID_STORED_DATA") &&
      message.includes("cohortDebriefAudioEnabled");

    if (!enabled || !institution || !isDebriefFlagCorruption || attemptedDebriefRepair || repairingDebriefFlags) {
      return;
    }

    setAttemptedDebriefRepair(true);
    (async () => {
      try {
        await runDebriefBackfill({
          institution: decodeURIComponent(institution),
          dryRun: false,
          cohortBatchSize: 500
        });
        toast({
          title: "Cohort data repaired",
          description: "Recovered legacy debrief-audio cohort flags. Reloading cohorts...",
          status: "info",
          duration: 3500,
          isClosable: true,
        });
        await refetchCohorts();
      } catch (repairError) {
        console.error("Failed to auto-repair cohort debrief flags:", repairError);
        toast({
          title: "Cohort repair failed",
          description: repairError?.message || "Unable to repair cohort data automatically.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    })();
  }, [
    cohortsError,
    enabled,
    institution,
    attemptedDebriefRepair,
    repairingDebriefFlags,
    runDebriefBackfill,
    refetchCohorts,
    toast
  ]);

  // Early return AFTER all hooks are declared
  if (!enabled) {
    return null;
  }

  const toggleExpanded = (cohortId) => {
    const newExpanded = new Set(expandedCohorts);
    if (newExpanded.has(cohortId)) {
      newExpanded.delete(cohortId);
    } else {
      newExpanded.add(cohortId);
    }
    setExpandedCohorts(newExpanded);
  };

  const handleDeleteCohort = async (cohort) => {
    if (!confirm(`Are you sure you want to delete the cohort "${cohort.displayName || cohort.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCohort({ id: cohort.id });
      toast({
        title: "Cohort Deleted",
        description: `Cohort "${cohort.displayName || cohort.name}" has been successfully deleted.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      refetchCohorts();
    } catch (error) {
      toast({
        title: "Error Deleting Cohort",
        description: error.message || "Failed to delete cohort",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (cohort) => {
    if (!cohort.isActive) {
      return <Badge colorScheme="red" variant="solid">Inactive</Badge>;
    }

    const now = new Date();
    const startDate = cohort.startDate ? new Date(cohort.startDate) : null;
    const endDate = cohort.endDate ? new Date(cohort.endDate) : null;

    if (startDate && now < startDate) {
      return <Badge colorScheme="yellow" variant="solid">Upcoming</Badge>;
    }
    if (endDate && now > endDate) {
      return <Badge colorScheme="gray" variant="solid">Ended</Badge>;
    }
    return <Badge colorScheme="green" variant="solid">Active</Badge>;
  };

  const getEligibilityTags = (shiftEligibility) => {
    if (!shiftEligibility) return [];

    const eligible = [];
    if (shiftEligibility.teamLeadEligible) eligible.push({ name: "Team Lead", color: "purple" });
    if (shiftEligibility.instructorEligible) eligible.push({ name: "Instructor", color: "green" });
    if (shiftEligibility.preceptorEligible) eligible.push({ name: "Preceptor", color: "orange" });
    if (shiftEligibility.fieldInternshipEligible) eligible.push({ name: "Field Internship", color: "cyan" });

    return eligible;
  };

  // Helper function to get agent access information for a cohort
  const getCohortAgentAccess = (cohortId) => {
    if (!cohortMemberships || !Array.isArray(cohortMemberships)) {
      return {
        totalUsers: 0,
        usersWithAgentAccess: 0,
        usersWithAssistantAccess: 0,
        agentAccessModes: [],
        assistantAccessModes: [],
        accessibleAgents: new Set(),
        accessibleAssistants: new Set()
      };
    }

    // Filter memberships for this specific cohort
    const cohortMembers = cohortMemberships.filter(
      membership => membership.cohort && membership.cohort.id === cohortId
    );

    // Extract users from memberships
    const cohortUsers = cohortMembers.map(membership => membership.user).filter(Boolean);

    const usersWithAgentAccess = cohortUsers.filter(user =>
      user.agentAccessMode && user.agentAccessMode !== 'none'
    );

    const usersWithAssistantAccess = cohortUsers.filter(user =>
      user.assistantAccessMode && user.assistantAccessMode !== 'none'
    );

    const agentAccessModes = [...new Set(cohortUsers
      .filter(user => user.agentAccessMode && user.agentAccessMode !== 'none')
      .map(user => user.agentAccessMode)
    )];

    const assistantAccessModes = [...new Set(cohortUsers
      .filter(user => user.assistantAccessMode && user.assistantAccessMode !== 'none')
      .map(user => user.assistantAccessMode)
    )];

    const accessibleAgents = new Set();
    cohortUsers.forEach(user => {
      if (user.accessibleAgentNames && Array.isArray(user.accessibleAgentNames)) {
        user.accessibleAgentNames.forEach(agent => {
          // Add agent names directly without normalization
          if (agent) {
            accessibleAgents.add(agent);
          }
        });
      }
    });

    const accessibleAssistants = new Set();
    cohortUsers.forEach(user => {
      if (user.accessibleAssistantIds && Array.isArray(user.accessibleAssistantIds)) {
        user.accessibleAssistantIds.forEach(assistant => {
          if (assistant) {
            accessibleAssistants.add(assistant);
          }
        });
      }
    });

    return {
      totalUsers: cohortUsers.length,
      usersWithAgentAccess: usersWithAgentAccess.length,
      usersWithAssistantAccess: usersWithAssistantAccess.length,
      agentAccessModes,
      assistantAccessModes,
      accessibleAgents,
      accessibleAssistants
    };
  };

  // Helper function to get agent access status badge
  const getAgentAccessBadge = (accessInfo) => {
    if (accessInfo.usersWithAgentAccess === 0 && accessInfo.usersWithAssistantAccess === 0) {
      return <Badge colorScheme="gray" variant="solid">No AI Access</Badge>;
    }

    const hasFullAccess = accessInfo.agentAccessModes.includes('all') ||
                         accessInfo.assistantAccessModes.includes('all');

    if (hasFullAccess) {
      return <Badge colorScheme="green" variant="solid">Full AI Access</Badge>;
    }

    if (accessInfo.usersWithAgentAccess > 0 || accessInfo.usersWithAssistantAccess > 0) {
      return <Badge colorScheme="blue" variant="solid">Limited AI Access</Badge>;
    }

    return <Badge colorScheme="gray" variant="solid">No AI Access</Badge>;
  };

  if (loadingCohorts) {
    return (
      <VStack spacing={4} align="center" py={8}>
        <Spinner size="lg" color="blue.400" />
        <Text color="gray.300">Loading cohorts...</Text>
      </VStack>
    );
  }

  if (cohortsError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold">Failed to load cohorts</Text>
          <Text fontSize="sm">{cohortsError.message}</Text>
        </VStack>
      </Alert>
    );
  }

  if (!cohorts || cohorts.length === 0 || !Array.isArray(cohorts)) {
    return (
      <Alert status="info">
        <AlertIcon />
        <Text>No cohorts found for this institution.</Text>
      </Alert>
    );
  }

  return (
    <>
      <VStack spacing={4} align="stretch">
        {safeArrayMap(cohorts, (cohort) => (
          <Card key={cohort.id} bg="gray.700" borderColor="gray.600">
            <CardHeader>
              <Flex align="center" justify="space-between">
                <VStack align="start" spacing={2}>
                  <HStack spacing={3} flexWrap="wrap">
                    <Heading size="sm" color="white">
                      {cohort.displayName || cohort.name}
                    </Heading>
                    {getStatusBadge(cohort)}
                    <Badge variant="outline" colorScheme="blue">
                      {cohort.cohortType}
                    </Badge>
                    {!loadingMemberships && cohortMemberships && getCohortAgentAccess(cohort.id).totalUsers > 0 && (
                      getAgentAccessBadge(getCohortAgentAccess(cohort.id))
                    )}
                  </HStack>
                  <HStack spacing={4}>
                    <Text fontSize="sm" color="gray.300">
                      {cohort.academicYear}
                    </Text>
                    <Text fontSize="sm" color="gray.300">
                      {formatDate(cohort.startDate)} - {formatDate(cohort.endDate)}
                    </Text>
                    {cohort.maxStudents && (
                      <Text fontSize="sm" color="gray.300">
                        Max: {cohort.maxStudents} students
                      </Text>
                    )}
                  </HStack>
                </VStack>
                <HStack spacing={2}>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<EditIcon />}
                      variant="ghost"
                      colorScheme="blue"
                      size="sm"
                    />
                    <Portal>
                      <MenuList
                        bg="gray.700"
                        borderColor="gray.600"
                        boxShadow="2xl"
                        sx={{
                          zIndex: 999999,
                          position: 'fixed !important',
                        }}
                      >
                        <MenuItem
                          bg="gray.700"
                          _hover={{ bg: "gray.600" }}
                          onClick={() => setEditCohortModal({ isOpen: true, cohort })}
                        >
                          Edit Cohort
                        </MenuItem>
                        <MenuItem
                          bg="gray.700"
                          _hover={{ bg: "gray.600" }}
                          onClick={() => setMemberModal({ isOpen: true, cohort })}
                        >
                          Manage Members
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem
                          bg="gray.700"
                          _hover={{ bg: "red.600" }}
                          color="red.400"
                          onClick={() => handleDeleteCohort(cohort)}
                        >
                          Delete Cohort
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                  <IconButton
                    icon={expandedCohorts.has(cohort.id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => toggleExpanded(cohort.id)}
                    variant="ghost"
                    size="sm"
                  />
                </HStack>
              </Flex>
            </CardHeader>

            <Collapse in={expandedCohorts.has(cohort.id)}>
              <CardBody pt={0}>
                <VStack spacing={4} align="stretch">
                  {/* Shift Eligibility */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                      Shift Eligibility:
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {safeArrayMap(getEligibilityTags(cohort.shiftEligibility), (tag) => (
                        <Tag key={tag.name} size="sm" colorScheme={tag.color}>
                          <TagLabel>{tag.name}</TagLabel>
                        </Tag>
                      ))}
                      {getEligibilityTags(cohort.shiftEligibility).length === 0 && (
                        <Text fontSize="sm" color="gray.400">No shift eligibilities set</Text>
                      )}
                    </HStack>
                  </Box>

                  {/* Per-User AI Agent Access Information */}
                  {!loadingMemberships && cohortMemberships && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                        Member AI Agent Access (Individual):
                      </Text>
                      <Text fontSize="xs" color="gray.400" mb={3}>
                        Shows aggregated individual user access. Each user's agent access is managed separately.
                      </Text>
                      {(() => {
                        const accessInfo = getCohortAgentAccess(cohort.id);
                        return (
                          <VStack spacing={3} align="stretch">
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="xs" color="gray.400">Total Members</Text>
                                <Text fontSize="sm" color="white" fontWeight="bold">
                                  {accessInfo.totalUsers}
                                </Text>
                              </VStack>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="xs" color="gray.400">Users w/ Agent Access</Text>
                                <Text fontSize="sm" color="white">
                                  {accessInfo.usersWithAgentAccess} / {accessInfo.totalUsers}
                                </Text>
                              </VStack>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="xs" color="gray.400">Users w/ Assistant Access</Text>
                                <Text fontSize="sm" color="white">
                                  {accessInfo.usersWithAssistantAccess} / {accessInfo.totalUsers}
                                </Text>
                              </VStack>
                              <VStack align="start" spacing={1}>
                                <Text fontSize="xs" color="gray.400">Overall Status</Text>
                                {getAgentAccessBadge(accessInfo)}
                              </VStack>
                            </SimpleGrid>

                            {/* Individual Access Summary */}
                            {(accessInfo.agentAccessModes.length > 0 || accessInfo.assistantAccessModes.length > 0) && (
                              <Box>
                                <Text fontSize="xs" color="gray.400" mb={2}>Active Access Types Among Members:</Text>
                                <HStack spacing={2} flexWrap="wrap">
                                  {accessInfo.agentAccessModes.map(mode => (
                                    <Tag key={`agent-${mode}`} size="sm" colorScheme="purple">
                                      <TagLabel>Agent: {mode}</TagLabel>
                                    </Tag>
                                  ))}
                                  {accessInfo.assistantAccessModes.map(mode => (
                                    <Tag key={`assistant-${mode}`} size="sm" colorScheme="teal">
                                      <TagLabel>Assistant: {mode}</TagLabel>
                                    </Tag>
                                  ))}
                                </HStack>
                              </Box>
                            )}

                            {/* Agents/Assistants in Use */}
                            {(accessInfo.accessibleAgents.size > 0 || accessInfo.accessibleAssistants.size > 0) && (
                              <Box>
                                <Text fontSize="xs" color="gray.400" mb={2}>AI Resources Accessible to Members:</Text>
                                <VStack spacing={2} align="stretch">
                                  {accessInfo.accessibleAgents.size > 0 && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.300" mb={1}>Agents in Use ({accessInfo.accessibleAgents.size}):</Text>
                                      <HStack spacing={1} flexWrap="wrap">
                                        {Array.from(accessInfo.accessibleAgents).map(agent => (
                                          <Tag key={`accessible-agent-${agent}`} size="xs" colorScheme="purple" variant="outline">
                                            <TagLabel>{agent}</TagLabel>
                                          </Tag>
                                        ))}
                                      </HStack>
                                    </Box>
                                  )}
                                  {accessInfo.accessibleAssistants.size > 0 && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.300" mb={1}>Assistants in Use ({accessInfo.accessibleAssistants.size}):</Text>
                                      <HStack spacing={1} flexWrap="wrap">
                                        {Array.from(accessInfo.accessibleAssistants).map(assistant => (
                                          <Tag key={`accessible-assistant-${assistant}`} size="xs" colorScheme="teal" variant="outline">
                                            <TagLabel>{assistant}</TagLabel>
                                          </Tag>
                                        ))}
                                      </HStack>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            )}

                            {accessInfo.totalUsers === 0 && (
                              <Alert status="info" size="sm">
                                <AlertIcon />
                                <Text fontSize="xs">No members in this cohort yet.</Text>
                              </Alert>
                            )}

                            {accessInfo.totalUsers > 0 && accessInfo.usersWithAgentAccess === 0 && accessInfo.usersWithAssistantAccess === 0 && (
                              <Alert status="warning" size="sm">
                                <AlertIcon />
                                <Text fontSize="xs">No members have AI agent access. Manage individual access through user management.</Text>
                              </Alert>
                            )}
                          </VStack>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Loading state for agent access */}
                  {loadingMemberships && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                        AI Agent Access:
                      </Text>
                      <HStack spacing={2}>
                        <Spinner size="sm" color="blue.400" />
                        <Text fontSize="sm" color="gray.400">Loading agent access information...</Text>
                      </HStack>
                    </Box>
                  )}

                  {/* FISDAP Calendar */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                      FISDAP Calendar:
                    </Text>
                    <HStack spacing={4} flexWrap="wrap">
                      <Badge
                        colorScheme={cohort.calendarSyncEnabled ? "green" : "gray"}
                        variant="subtle"
                      >
                        {cohort.calendarSyncEnabled ? "Sync Enabled" : "Sync Disabled"}
                      </Badge>
                      {cohort.calendarSyncStatus && cohort.calendarSyncStatus !== "idle" && (
                        <Badge
                          colorScheme={
                            cohort.calendarSyncStatus === "success" ? "green" :
                            cohort.calendarSyncStatus === "error" ? "red" :
                            cohort.calendarSyncStatus === "syncing" ? "yellow" : "gray"
                          }
                          variant="outline"
                        >
                          {cohort.calendarSyncStatus}
                        </Badge>
                      )}
                      {cohort.lastCalendarSyncAt && (
                        <Text fontSize="xs" color="gray.400">
                          Last sync: {formatDate(cohort.lastCalendarSyncAt)}
                        </Text>
                      )}
                      {cohort.fisdapCalendarUrl && (
                        <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="200px">
                          URL configured
                        </Text>
                      )}
                      <Link to="/admin/calendar-sync" style={{ textDecoration: 'none' }}>
                        <Text fontSize="xs" color="blue.300" _hover={{ textDecoration: "underline" }}>
                          Configure Calendar Sync →
                        </Text>
                      </Link>
                    </HStack>
                  </Box>

                  {/* Additional Settings */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    {isSystemAdmin(session) && (
                      <VStack align="start" spacing={1}>
                        <Text fontSize="xs" color="gray.400">Clinical Doc Notifications</Text>
                        <Badge colorScheme={cohort.enableClinicalDocNotifications ? "green" : "red"} variant="subtle">
                          {cohort.enableClinicalDocNotifications ? "Enabled" : "Disabled"}
                        </Badge>
                      </VStack>
                    )}
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.400">Live Debrief Audio</Text>
                      <Badge colorScheme={cohort.cohortDebriefAudioEnabled === false ? "red" : "green"} variant="subtle">
                        {cohort.cohortDebriefAudioEnabled === false ? "Disabled" : "Enabled"}
                      </Badge>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.400">Created</Text>
                      <Text fontSize="sm" color="white">
                        {formatDate(cohort.createdAt)}
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.400">Updated</Text>
                      <Text fontSize="sm" color="white">
                        {formatDate(cohort.updatedAt)}
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="gray.400">Internal ID</Text>
                      <Text fontSize="xs" color="gray.300" fontFamily="mono">
                        {cohort.name}
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Collapse>
          </Card>
        ))}
      </VStack>

      {/* Edit Cohort Modal */}
      <CohortEditModal
        isOpen={editCohortModal.isOpen}
        onClose={() => setEditCohortModal({ isOpen: false, cohort: null })}
        cohort={editCohortModal.cohort}
        onCohortUpdated={async () => {
          // Refetch cohorts and wait for fresh data
          const freshData = await refetchCohorts();

          // Find the updated cohort in the fresh data
          if (editCohortModal.cohort && freshData) {
            const updatedCohort = freshData.find(c => c.id === editCohortModal.cohort.id);
            if (updatedCohort) {
              // Update the modal with fresh cohort data so reopening shows current values
              setEditCohortModal(prev => ({ ...prev, cohort: updatedCohort }));
            }
          }

          // Modal closes itself in handleSubmit, so we don't need to close it here
        }}
        session={session}
        institutionConfig={institutionConfig}
      />

      {/* Member Management Modal */}
      <MemberManagementModal
        isOpen={memberModal.isOpen}
        onClose={() => setMemberModal({ isOpen: false, cohort: null })}
        cohort={memberModal.cohort}
        onMemberUpdate={debouncedRefetch}
      />
    </>
  );
}

export default CohortList;
